import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { resolverStackFuenteContrato } from './contratoFuentes';
import { construirContenidoVistaPreviaContratoResuelto } from './plantillaContratoPreview';
import { CONTRATO_A4, obtenerMedidasPaginaContrato } from './contratoPaginaA4';
import { calcularSaltosPaginaContrato, alturaContenidoPagina } from './contratoPaginacionPreview';
import { refinarSaltosConCanvas, sliceTieneTinta, encontrarUltimaFilaConTinta } from './contratoCanvasCorte';

/**
 * Escala de captura fija (no devicePixelRatio).
 * Misma nitidez en todas las PCs; el layout usa CSS px a 96 DPI.
 */
const PDF_HTML2CANVAS_SCALE = 3;

function formatearFecha(fecha) {
  if (!fecha) return '';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  if (!y || !m || !d) return texto;
  return `${d}/${m}/${y}`;
}

function construirMapaContrato({ empresa, empleado, contrato }) {
  if (contrato?.variables_resueltas && Object.keys(contrato.variables_resueltas).length > 0) {
    return contrato.variables_resueltas;
  }

  return {
    'empresa.nombre_empresa': empresa?.nombre_empresa || empresa?.name || empresa?.nombre || '',
    'empresa.ruc': empresa?.ruc || '',
    'empresa.representante_legal': empresa?.representante_legal || empresa?.representante || '',
    'empresa.direccion': empresa?.direccion || empresa?.address || '',
    'empresa.dni_representante': empresa?.dni_representante || '',
    'empleado.nombre_completo': empleado?.nombre_completo || '',
    'empleado.tipo_documento': empleado?.tipo_documento || '',
    'empleado.numero_documento': empleado?.numero_documento || '',
    'contrato.lugar_firma': contrato?.lugar_firma || '',
    'contrato.fecha_firma': formatearFecha(contrato?.fecha_firma),
  };
}

export function buildHtmlContratoEmpleado({ empresa, empleado, contrato, firmaEmpresaBase64 }) {
  return construirContenidoVistaPreviaContratoResuelto({
    tipo: contrato?.tipo,
    titulo: contrato?.titulo,
    introduccion: contrato?.introduccion,
    articulos: contrato?.articulos,
    mapa: construirMapaContrato({ empresa, empleado, contrato }),
    fuente: contrato?.fuente,
    firmaEmpleadorSrc: firmaEmpresaBase64 || null,
  });
}

function cargarImagen(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = src;
  });
}

async function dibujarMarcaDeAgua(ctx, logoSrc, anchoCanvas, altoCanvas) {
  try {
    const img = await cargarImagen(logoSrc);
    const maxW = anchoCanvas * 0.58;
    const maxH = altoCanvas * 0.72;
    const ratio = Math.min(maxW / img.width, maxH / img.height);
    const w = img.width * ratio;
    const h = img.height * ratio;
    const x = (anchoCanvas - w) / 2;
    const y = (altoCanvas - h) / 2;

    ctx.save();
    ctx.globalAlpha = 0.1;
    ctx.drawImage(img, x, y, w, h);
    ctx.restore();
  } catch (_) {
    // Sin marca de agua si el logo falla
  }
}

/**
 * Monta el HTML en un iframe aislado (sin CSS del app ni zoom residual del body).
 * Ancho fijo en CSS px → mismo layout en todas las PCs.
 */
async function montarContenedorContratoPdf(htmlContenido, { fontFamily, anchoPx }) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    `width:${anchoPx}px`,
    'height:200px',
    'border:0',
    'margin:0',
    'padding:0',
    'opacity:0',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error('No se pudo crear el contenedor del PDF');
  }

  doc.open();
  doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
    width: ${anchoPx}px;
    overflow: hidden;
    zoom: 1;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }
</style>
</head><body></body></html>`);
  doc.close();

  const wrap = doc.createElement('div');
  wrap.setAttribute('data-contrato-pdf-root', '1');
  wrap.style.cssText = [
    `width:${anchoPx}px`,
    'box-sizing:border-box',
    'background:#ffffff',
    `font-family:${fontFamily}`,
    '-webkit-font-smoothing:antialiased',
    'text-rendering:geometricPrecision',
    'zoom:1',
    '-webkit-text-size-adjust:100%',
    'text-size-adjust:100%',
  ].join(';');
  wrap.innerHTML = htmlContenido;
  doc.body.appendChild(wrap);

  if (doc.fonts?.ready) {
    await doc.fonts.ready;
  }

  // Forzar layout estable antes de medir
  void wrap.offsetHeight;
  const total = wrap.scrollHeight || wrap.offsetHeight || 0;
  iframe.style.height = `${Math.max(200, total + 24)}px`;
  void wrap.offsetHeight;

  return {
    wrap,
    total,
    cleanup: () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    },
  };
}

/**
 * Captura el contenido una vez, refina saltos con análisis de tinta del canvas
 * y arma cada hoja A4 sin partir glifos.
 */
async function htmlPaginadoA4Pdf(htmlContenido, { fuente, logoEmpresaBase64 }) {
  const medidas = obtenerMedidasPaginaContrato();
  const fontFamily = resolverStackFuenteContrato(fuente);
  const pdf = new jsPDF('p', 'mm', 'a4');
  const scale = PDF_HTML2CANVAS_SCALE;
  const anchoCss = Math.round(medidas.anchoContenidoPx);

  const { wrap, cleanup } = await montarContenedorContratoPdf(htmlContenido, {
    fontFamily,
    anchoPx: anchoCss,
  });

  try {
    const total = wrap.scrollHeight || wrap.offsetHeight || 0;
    const saltosDom = calcularSaltosPaginaContrato(wrap, medidas.altoContenidoPx);

    const contentCanvas = await html2canvas(wrap, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: anchoCss,
      height: total,
      windowWidth: anchoCss,
      windowHeight: total,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      imageTimeout: 0,
      foreignObjectRendering: false,
      onclone: (clonedDoc) => {
        const nodo =
          clonedDoc.querySelector('[data-contrato-pdf-root]') ||
          clonedDoc.body?.firstElementChild;
        if (!nodo) return;
        nodo.style.setProperty('zoom', '1');
        nodo.style.setProperty('-webkit-text-size-adjust', '100%');
        nodo.style.setProperty('text-size-adjust', '100%');
        nodo.style.setProperty('-webkit-font-smoothing', 'antialiased');
        nodo.style.setProperty('text-rendering', 'geometricPrecision');
        nodo.style.width = `${anchoCss}px`;
        nodo.style.opacity = '1';
        if (clonedDoc.documentElement) {
          clonedDoc.documentElement.style.zoom = '1';
        }
        if (clonedDoc.body) {
          clonedDoc.body.style.zoom = '1';
          clonedDoc.body.style.margin = '0';
          clonedDoc.body.style.padding = '0';
        }
      },
    });

    const contentW = Math.round(anchoCss * scale);
    const ultimaTintaPx = encontrarUltimaFilaConTinta(contentCanvas, contentW);
    const totalEfectivo =
      ultimaTintaPx >= 0 ? Math.min(total, ultimaTintaPx / scale + 2) : total;

    const paginas = refinarSaltosConCanvas({
      saltosDom: saltosDom.length > 0 ? saltosDom : [0],
      totalCss: totalEfectivo,
      canvas: contentCanvas,
      scale,
      contentWidthCanvas: contentW,
      altoContenidoCss: medidas.altoContenidoPx,
    });

    const pageW = Math.round(medidas.anchoPx * scale);
    const pageH = Math.round(medidas.altoPx * scale);
    const padL = Math.round(medidas.paddingLeftPx * scale);
    const padT = Math.round(medidas.paddingTopPx * scale);

    let hojasEmitidas = 0;

    for (let indice = 0; indice < paginas.length; indice += 1) {
      const offsetY = paginas[indice];
      const altoVisible = alturaContenidoPagina(
        paginas,
        indice,
        medidas.altoContenidoPx,
        totalEfectivo
      );

      const srcY = Math.max(0, Math.floor(offsetY * scale));
      const srcHDeseado = Math.max(0, Math.floor(altoVisible * scale));
      const srcH = Math.min(srcHDeseado, Math.max(0, contentCanvas.height - srcY));

      // Evitar hoja en blanco / casi vacía (margen residual / salto de más)
      if (srcH <= 0 || !sliceTieneTinta(contentCanvas, srcY, srcH, contentW)) {
        continue;
      }

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = pageW;
      pageCanvas.height = pageH;
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageW, pageH);
      ctx.imageSmoothingEnabled = false;

      ctx.drawImage(
        contentCanvas,
        0,
        srcY,
        Math.min(contentW, contentCanvas.width),
        srcH,
        padL,
        padT,
        Math.min(contentW, contentCanvas.width),
        srcH
      );

      if (logoEmpresaBase64) {
        await dibujarMarcaDeAgua(ctx, logoEmpresaBase64, pageW, pageH);
      }

      if (hojasEmitidas > 0) {
        pdf.addPage();
      }

      pdf.addImage(
        pageCanvas.toDataURL('image/png'),
        'PNG',
        0,
        0,
        CONTRATO_A4.anchoMm,
        CONTRATO_A4.altoMm,
        undefined,
        'NONE'
      );
      hojasEmitidas += 1;
    }

    if (hojasEmitidas === 0) {
      pdf.setFontSize(12);
      pdf.text('Contrato sin contenido visible.', 20, 20);
    }

    return pdf;
  } finally {
    cleanup();
  }
}

export async function exportarContratoEmpleadoPdf({
  empresa,
  empleado,
  contrato,
  firmaEmpresaBase64,
  logoEmpresaBase64,
}) {
  const html = buildHtmlContratoEmpleado({
    empresa,
    empleado,
    contrato,
    firmaEmpresaBase64,
  });

  const pdf = await htmlPaginadoA4Pdf(html, {
    fuente: contrato?.fuente,
    logoEmpresaBase64,
  });

  const nombreEmpleado = (empleado?.nombre_completo || 'empleado').replace(/\s+/g, '_').slice(0, 40);
  const titulo = (contrato?.titulo || 'Contrato')
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_\s-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 40);
  const fecha = new Date().toISOString().split('T')[0];
  pdf.save(`Contrato_${nombreEmpleado}_${titulo}_${fecha}.pdf`);
}
